import styled from "@emotion/styled";
import { Button, Typography } from "@mindlercare/ui-web";
import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useAuth, useInternalAuth } from "../AuthenticationProvider";
import { Spinner } from "../components/Spinner";
import { useTranslation } from "../hooks/useTranslation";
import { Translations } from "../translations";

export const LoginWithQrCode = () => {
  const {
    startBankIdSignIn,
    getNextQrCode,
    cancelSignInWithBankId,
    onBankIdSignInStateChange,
    setPendingStatus,
  } = useInternalAuth();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { t } = useTranslation();
  const { loginState } = useAuth();

  useEffect(() => {
    const unsubscribe = onBankIdSignInStateChange((state) => {
      if (state.status === "failed") {
        setQrCode(null);

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    });

    return () => unsubscribe();
  }, [onBankIdSignInStateChange]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(async () => {
      try {
        const r = await getNextQrCode();
        if (!r) return;

        setQrCode(r.nextCode);
      } catch (e) {
        if (e instanceof Error) {
          console.error(e.message, e);
        }
        // intervalRef.current is defined by the laws of closure
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setQrCode(null);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [getNextQrCode]);

  return (
    <>
      <Container>
        <Typography variant="h3">{t("LOGIN_WITH_QRCODE")}</Typography>
        <ol>
          {Object.keys(Translations.en)
            .filter((key) => key.startsWith("LOGIN_WITH_QRCODE_STEPS"))
            .map((_, index) => (
              <li key={index}>
                <Typography variant="body-small">
                  {t(`LOGIN_WITH_QRCODE_STEPS_${index as 0 | 1 | 2}`)}
                </Typography>
              </li>
            ))}
        </ol>
      </Container>
      {!!qrCode && <QrCode value={qrCode} />}
      {loginState === "login-in-progress" && !qrCode && <Spinner />}
      {loginState === "login-in-progress" ? (
        <Button
          variant="secondary-small"
          onClick={() => {
            cancelSignInWithBankId();
            setQrCode(null);
          }}
        >
          {t("CANCEL")}
        </Button>
      ) : (
        <Button
          variant="primary"
          onClick={async () => {
            setPendingStatus();

            const r = await startBankIdSignIn();
            if (r) {
              setQrCode(r.startQrCode);
            }
          }}
        >
          {t("CREATE_QR_CODE")}
        </Button>
      )}
    </>
  );
};

const QrCode = styled(QRCode)`
  border: 10px solid #f0affa;
  border-radius: 14px;
  width: 156px; // so that actual QR code is 128x128
  height: 156px; // so that actual QR code is 128x128
  padding: 4px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & ol {
    margin-left: -24px;
    margin-block-start: 0px;
    margin-block-end: 0px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: #554f4d;
  }
`;
